import * as THREE from "three"
import { getEntityMaxWidth, getEntityTotalLength } from "../helpers/entity-geometry.helper"
import { makeHighlightMaterial, makeHighlightEdgeMaterial } from "../helpers/overlay-materials.helper"
import { paintLane } from "../helpers/lane-painter.helper"

// Resaltado un poco más ancho que la línea original (más visible).
const HIGHLIGHT_WIDTH_FACTOR = 1.4

/**
 * Overlay de RENDER del resaltado del footer (dropdown "Resaltar") sobre el visor
 * Three.js. Engine-adjacent, hermano de AdvanceOverlay: recibe un getter al
 * DxfViewer vivo y POSEE sus propios THREE.Groups (fuera de Vue). El componente
 * decide QUÉ entidades resaltar y con qué color; esta clase solo PINTA y DISPONE.
 */
export class HighlightOverlay {
  /** @param {() => (object|null)} getDxf - devuelve el DxfViewer vivo (o null). */
  constructor(getDxf) {
    this._getDxf = getDxf
    this.meshes = Object.create(null)   // handle(string) → THREE.Group
  }

  /**
   * Pinta un ribbon de color uniforme cubriendo toda la polilínea: relleno a
   * ancho completo + dos bordes finos y brillantes en los cantos. Los quads se
   * elevan por encima de los carriles de avance para que el color uniforme
   * domine visualmente. No descarta lo previo del handle (lo hace disposeAll).
   * @param {string|number} handle
   * @param {object} entity
   * @param {number} colorInt
   */
  paintEntity(handle, entity, colorInt) {
    const dxf    = this._getDxf?.()
    const scene  = dxf?.GetScene?.()
    const origin = dxf?.GetOrigin?.() || { x: 0, y: 0 }
    if (!scene) return

    const totalLength = getEntityTotalLength(entity)
    if (totalLength <= 0) return
    const width = Math.max(getEntityMaxWidth(entity), 0.1) * HIGHLIGHT_WIDTH_FACTOR

    const group = new THREE.Group()
    group.name = 'HighlightOverlay_' + handle
    const fillMat = makeHighlightMaterial(colorInt)
    const edgeMat = makeHighlightEdgeMaterial(colorInt)
    group.userData.materials = [fillMat, edgeMat]

    // Relleno a ancho completo.
    paintLane(group, entity.vertices, origin, totalLength, width, 0, fillMat, 0, null)
    // Borde: dos franjas finas y brillantes en los cantos → remarca la línea.
    const edgeW = Math.max(width * 0.16, 0.08)
    const off   = (width - edgeW) / 2
    paintLane(group, entity.vertices, origin, totalLength, edgeW,  off, edgeMat, 1, null)
    paintLane(group, entity.vertices, origin, totalLength, edgeW, -off, edgeMat, 1, null)

    // Todo por encima de los carriles de avance (z 0.5, renderOrder 600); el
    // borde brillante por encima del relleno para que remarque el contorno.
    group.traverse((obj) => {
      if (!obj.isMesh) return
      const isEdge = obj.material === edgeMat
      obj.position.z   = isEdge ? 0.82 : 0.80
      obj.renderOrder  = isEdge ? 812  : 800
    })

    scene.add(group)
    this.meshes[handle] = group
  }

  /** Quita y dispone todos los resaltados. Fuerza render. */
  disposeAll() {
    const dxf   = this._getDxf?.()
    const scene = dxf?.GetScene?.()
    for (const h of Object.keys(this.meshes)) {
      const group = this.meshes[h]
      if (!group) continue
      if (scene) scene.remove(group)
      group.traverse((obj) => { if (obj.geometry) obj.geometry.dispose?.() })
      for (const m of (group.userData.materials || [])) m?.dispose?.()
      delete this.meshes[h]
    }
    dxf?.Render?.()
  }
}
